import supabase from '../supabase/supabase';
import { track, trackError } from './analytics';
import { readTrackingSession } from './tracking-hub';

export const TIER_ORDER = ['inicial', 'rotina', 'pro'];

export const normalizeTier = (t) => {
  const v = (t || 'rotina').toLowerCase();
  return v === 'profissional' ? 'pro' : v === 'free' ? 'rotina' : v;
};

const readJson = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || 'Não foi possível concluir a operação de pagamento.');
    error.code = data.code || `http_${res.status}`;
    throw error;
  }
  return data;
};

const PLAN_VALUE_BRL = {
  inicial: { mensal: 47, anual: 470 },
  rotina: { mensal: 97, anual: 970 },
  pro: { mensal: 247, anual: 2470 },
};

const authGetJson = async (url, token) => readJson(await fetch(url, {
  headers: { Authorization: `Bearer ${token}` },
}));

const authPostJson = async (url, token, body) => readJson(await fetch(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  body: JSON.stringify(body),
}));

const checkoutResult = (result, target, frequency) => {
  track('checkout_created', {
    plan_tier: target,
    frequency,
    checkout_id_present: Boolean(result?.checkout_id),
    amount_cents: Number(result?.amount_cents || 0),
  });
  return { status: 'redirect', url: result?.url };
};

export async function requestPlan({ tier, frequency, billingCustomer, coupon, expectedValue }) {
  const target = normalizeTier(tier);
  const suffix = frequency === 'anual' ? '_annual' : frequency === 'mensal' ? '_monthly' : null;
  if (!TIER_ORDER.includes(target) || !suffix) throw new Error('Plano ou frequência inválidos.');

  track('plan_selected', {
    plan_id: target,
    period: frequency === 'anual' ? 'annual' : 'monthly',
    has_trial: true,
  });
  track('checkout_started', { plan_id: target, value: Number.isFinite(expectedValue) ? expectedValue : PLAN_VALUE_BRL[target]?.[frequency === 'anual' ? 'anual' : 'mensal'], currency: 'BRL', has_coupon: Boolean(coupon) });
  track('plan_checkout_submitted', {
    plan_tier: target,
    frequency,
    product: `${target}${suffix}`,
  });

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    track('checkout_requires_login', { plan_tier: target, frequency });
    return { status: 'login' };
  }

  try {
    const token = session.access_token;
    const entitlement = await authGetJson('/.netlify/functions/billing-entitlement', token);
    const selectedProduct = `${target}${suffix}`;
    const activeEntitlements = Array.isArray(entitlement?.entitlements)
      ? entitlement.entitlements.filter((item) => item?.status === 'active')
      : [];
    const alreadyActive = entitlement?.source === 'admin'
      ? normalizeTier(entitlement?.tier) === target
      : activeEntitlements.some((item) => item?.product === selectedProduct);
    if (alreadyActive) {
      track('plan_already_active', { plan_tier: target, frequency });
      return { status: 'current' };
    }

    if (entitlement?.source === 'admin') {
      track('checkout_blocked_admin_grant', { plan_tier: target, frequency });
      return { status: 'manual_grant' };
    }

    if (activeEntitlements.length > 0) {
      const current = activeEntitlements[0];
      track('checkout_requires_plan_management', {
        plan_tier: target,
        frequency,
        current_product: current?.product || 'unknown',
      });
      return {
        status: 'manage',
        product: current?.product || null,
      };
    }

    const idempotencyKey = crypto.randomUUID();
    const payload = { tier, frequency, billingCustomer, idempotency_key: idempotencyKey, tracking_session: readTrackingSession(), ...(coupon ? { coupon } : {}) };
    try {
      return checkoutResult(await authPostJson('/.netlify/functions/create-subscription', token, payload), target, frequency);
    } catch (error) {
      if (!(error instanceof TypeError)) throw error;
      track('checkout_network_retry', { plan_tier: target, frequency });
      return checkoutResult(await authPostJson('/.netlify/functions/create-subscription', token, payload), target, frequency);
    }
  } catch (error) {
    const errorCode = String(error?.code || error?.name || 'unknown_error');
    trackError('checkout_creation', error, { plan_tier: target, frequency });
    track('checkout_failed', {
      plan_tier: target,
      frequency,
      error_code: errorCode,
    });
    track('billing_issue', {
      plan_id: target,
      error_code: errorCode,
    });
    throw error;
  }
}
